import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import SocialMediaSection from '../components/SocialMediaSection';
import { locations } from '../data/mock';
import { MapPin, Phone, Clock, Star, ArrowLeft, ArrowRight } from 'lucide-react';

const VENUE_IMAGES = [
  "https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/wgn5ctmv_WhatsApp%20Image%202026-03-26%20at%2012.30.53%20%282%29.jpeg",
  "https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/ig40hm8q_WhatsApp%20Image%202026-03-26%20at%2012.30.53%20%283%29.jpeg",
  "https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/6nm4g6t5_WhatsApp%20Image%202026-03-26%20at%2012.30.52%20%282%29.jpeg",
  "https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/gtr04py8_WhatsApp%20Image%202026-03-26%20at%2012.30.52%20%281%29.jpeg"
];

const LAT = 17.44764860443804;
const LNG = 78.36304744417907;
const MAPS_EMBED_URL = `https://maps.google.com/maps?q=${LAT},${LNG}&z=16&output=embed`;
const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${LAT},${LNG}&destination_place_id=`;

const LocationPage = () => {
  const { city } = useParams();
  const location = locations.find(l => l.name.toLowerCase() === city?.toLowerCase());

  if (!location) {
    return (
      <Layout>
        <div className="min-h-screen paper-texture pt-32 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-display text-4xl mb-4" style={{ color: '#1A1918' }}>Location Not Found</h1>
            <Link to="/" className="font-mono text-sm" style={{ color: '#EE2535' }}>Go back home</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title="Casa Loco Hyderabad | Mexican Restaurant at Radisson Hitec City, Gachibowli"
        description="Visit Casa Loco at 2nd Floor, Radisson Hitec City, Gachibowli, Hyderabad. Authentic Mexican tacos, burritos, margaritas and more. Get directions and hours."
        path="/location/hyderabad"
      />
      <div className="min-h-screen paper-texture" style={{ paddingTop: '76px' }} data-testid="location-page">
        {/* Hero Banner */}
        <div className="relative h-[50vh] sm:h-[60vh] overflow-hidden" style={{ borderBottom: '4px solid #1A1918' }}>
          <img
            src={VENUE_IMAGES[0]}
            alt={`Casaloco ${location.name}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-16">
            <div className="max-w-7xl mx-auto">
              <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-4 font-mono text-xs uppercase tracking-wider transition-colors">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-white" style={{ textShadow: '3px 3px 0 rgba(0,0,0,0.5)' }}>
                CASALOCO {location.name.toUpperCase()}
              </h1>
              <div className="flex items-center gap-1 mt-3">
                {[...Array(location.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5" fill="#F7B638" stroke="none" />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Location Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Info Card */}
            <div className="bg-white p-8" style={{ border: '3px solid #1A1918', boxShadow: '5px 5px 0 #1A1918' }} data-testid="location-info-card">
              <h2 className="font-display text-2xl sm:text-3xl mb-6" style={{ color: '#1A1918' }}>Visit Us</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#EE2535' }} />
                  <div>
                    <h4 className="font-mono font-bold text-sm" style={{ color: '#1A1918' }}>Address</h4>
                    <p className="font-mono text-[#4A4641] text-sm">2nd Floor, Radisson Hitec City, Gachibowli, Hyderabad - 500084</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#EE2535' }} />
                  <div>
                    <h4 className="font-mono font-bold text-sm" style={{ color: '#1A1918' }}>Phone</h4>
                    <a href={`tel:${location.phone}`} className="font-mono text-[#4A4641] text-sm hover:text-[#EE2535] transition-colors">{location.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#EE2535' }} />
                  <div>
                    <h4 className="font-mono font-bold text-sm" style={{ color: '#1A1918' }}>Hours</h4>
                    <p className="font-mono text-[#4A4641] text-sm">{location.hours}</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={MAPS_DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="px-6 py-3 font-display tracking-wider text-white text-base transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
                  data-testid="location-directions-btn"
                  style={{ backgroundColor: '#EE2535', border: '2px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}>
                  GET DIRECTIONS
                </a>
                <Link to="/food-menu" className="px-6 py-3 font-display tracking-wider text-base transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
                  data-testid="location-menu-btn"
                  style={{ backgroundColor: '#F7B638', color: '#1A1918', border: '2px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}>
                  VIEW MENU
                </Link>
              </div>
            </div>

            {/* Map */}
            <div style={{ border: '3px solid #1A1918', boxShadow: '5px 5px 0 #1A1918' }} data-testid="location-map">
              <iframe
                src={MAPS_EMBED_URL}
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block', minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Casa Loco Hyderabad Location"
              />
            </div>
          </div>
        </div>

        {/* Follow the Fiesta */}
        <SocialMediaSection />

        {/* Join the Family */}
        <div className="py-12 text-center" style={{ backgroundColor: '#EE2535', borderTop: '4px solid #1A1918' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-4" style={{ textShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}>
              JOIN THE CASA LOCO FAMILY!
            </h2>
            <p className="font-mono text-white/90 text-sm max-w-xl mx-auto mb-6">
              We're always looking for enthusiastic and talented individuals passionate about Mexican cuisine and culture.
            </p>
            <a href="mailto:hola@casaloco.in" className="inline-block px-8 py-3 font-display tracking-wider text-base transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
              data-testid="loc-join-btn"
              style={{ backgroundColor: '#F7B638', color: '#1A1918', border: '3px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}>
              JOIN US IN HYDERABAD
            </a>
          </div>
        </div>

        {/* Menu CTA */}
        <div className="py-10 text-center" style={{ backgroundColor: '#51A262', borderTop: '4px solid #1A1918' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-center gap-4">
            <Link to="/food-menu" className="inline-flex items-center gap-2 px-8 py-3 font-display tracking-wider text-base transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
              style={{ backgroundColor: '#F7B638', color: '#1A1918', border: '3px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}>
              FOOD MENU <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/drinks-menu" className="inline-flex items-center gap-2 px-8 py-3 font-display tracking-wider text-base transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
              style={{ backgroundColor: 'white', color: '#1A1918', border: '3px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}>
              DRINKS MENU <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LocationPage;